// oxlint-disable-next-line lf/sc
// Shared types, SPI, and diagnostics for the Poisson solver dispatcher.
//
// This module holds the cross-backend contract surface: `SolverConfig`, `SolveOptions` /
// `SolveResult`, the `SolverBackend` SPI slot + accessors, the `solverDiagnostics` test
// instrumentation counters, and the `SolveAbortedError` cooperative-cancellation tag.

import type {AMRGrid} from "../AMRGrid.js";
import {CellType} from "../AMRGrid.js";
import type {CachedLevel} from "../Hierarchy/Hierarchy_Common.js";

/** **2026-07-30 — any `CellType.SoftPin` cell forces CPU fallback** (WGSL/WGL shaders
 *  lack the soft-pin `k·(H−target)` penalty term; the CPU path handles it).  Gates the
 *  GPU/WGL fast paths via `solveAsyncLocked`.  ~µs linear scan over `cells.count`. */
export function gridHasSoftPin(grid: AMRGrid): boolean {
	const type = grid.cells.type;
	const n = grid.cells.count;
	for (let i = 0; i < n; i++) {
		if (type[i] === CellType.SoftPin) return true;
	}
	return false;
}


/** **Set6 — the three computing backends a V-cycle phase can route to:** `"cpu"`
 *  (reference CPU path), `"webGL"` (WebGL2 GLSL-ES-300 fragment shaders — the fallback
 *  for unstable-WebGPU hosts), `"webGPU"` (WGSL compute shaders via `GPUSmootherBackend`). */
export type ComponentBackend = "cpu" | "webGL" | "webGPU";

/** **Set6 — per-V-cycle-phase backend selection**: each field routes one V-cycle phase
 *  (`fineSmoother`/`galSmoother` GS sweeps, `galerkinBuild`, `hierarchy`, `prolong`/`residual`/
 *  `restrict`, `solve`) with CPU fallback when a backend isn't registered (plan §5). */
export interface ComponentBackendSelection {
	fineSmoother: ComponentBackend;
	galerkinBuild: ComponentBackend;
	galSmoother: ComponentBackend;
	hierarchy: ComponentBackend;
	prolong: ComponentBackend;
	residual: ComponentBackend;
	restrict: ComponentBackend;
	solve: ComponentBackend;
}

/** **Set6 — lex (per-cell sequential) vs wavefront (hyperplane-coloured, parallel) coarse
 *  smoother variant; a PREFERENCE honoured only where the backend ships both (WebGPU ships
 *  both; WebGL wavefront-only).  See `Set3_WebGPU.md` Step 2 for the algebra. */
export type SmootherVariant = "lex" | "wavefront";


/** Solver configuration (mirrors `PoissonSolverConfig` in the data-model layer for the
 *  pure-solver unit tests — the persisted class adds outer-BC / refinement-band fields that
 *  the rasterizer uses, not the solver itself). */
export interface SolverConfig {
	/** Multigrid V-cycle count per solve. */
	vCycles: number;
	/** Pre-smoothing Gauss-Seidel sweeps per V-cycle level. */
	preSmoothSweeps: number;
	/** Post-smoothing sweeps per V-cycle level. */
	postSmoothSweeps: number;
	/** Coarsest-level direct-solve threshold: solve directly with dense LU below this size. */
	coarseDirectSolveThreshold: number;
	/** Convergence tolerance (L² residual). Stop early if residual drops below this. */
	residualTolerance: number;
}

export const DEFAULT_SOLVER_CONFIG: SolverConfig = {
	vCycles: 3,
	preSmoothSweeps: 3,
	postSmoothSweeps: 3,
	coarseDirectSolveThreshold: 64,
	residualTolerance: 1e-4,
};

export interface SolveResult {
	/** Final L² residual norm after the last V-cycle. */
	finalResidual: number;
	/** Number of V-cycles actually run (may be < cfg.vCycles if tolerance reached early). */
	cyclesRun: number;
	/** Residual history (one entry per V-cycle). */
	residualHistory: number[];
	/** The coarse hierarchy actually used: the accepted `providedHierarchy` if one was
	 *  passed, else the freshly-built one. Cache it (with `workingGridFingerprint`) for reuse. */
	hierarchy?: CachedLevel[];
	/** **Set3 Step 1 — which backend actually ran the solve:** `"cpu"` ({@link solvePoissonCPU}),
	 *  `"gpu"` (WGSL `GPUSmootherBackend`), `"webgl"` (WebGL2 fallback-for-unstable-WebGPU),
	 *  or `undefined` (pre-Set3 callers; treated as `"cpu"`).  Test lanes assert this to catch silent CPU fallback. */
	backend?: "cpu" | "gpu" | "webgl";
}

/** Solve the Poisson/Laplace equation on `grid` in-place, running `cfg.vCycles` V-cycles
 *  (early-stop at `cfg.residualTolerance`).  Perf: {@link buildCoarseHierarchy} builds all
 *  coarse levels once; per-V-cycle only `coarse.cells.H`/`f` change — no structural rebuild. */
export interface SolveOptions {
	/** Optional pre-built coarse hierarchy, reused on a structurally-identical grid: when
	 *  `providedHierarchyFingerprint` matches, the expensive `buildCoarseHierarchy` is skipped.
	 *  Caller: `workingGridFingerprint(grid, leaves)`; reuse iff fp matches; omitted fp = unconditionally. */
	providedHierarchy?: CachedLevel[];
	providedHierarchyFingerprint?: string;
	/** **Set3 Step 1 — the "no silent CPU fallback" guard.**  When `true`, {@link solvePoisson}
	 *  throws instead of falling through to {@link solvePoissonCPU} — used by the WebGPU browser
	 *  test lane so a broken gate/packCells surfaces loudly.  Production callers NEVER pass this; fallback is desired. */
	requireGPU?: boolean;
	/** **Set3 (2026-07-24) — camera-pan latest-job-wins cancellation; aborting throws
	 *  {@link SolveAbortedError} at the next `signal.aborted` gate.  **2026-08-03** now a
	 *  {@link VetoSignal} with `maybeYield()` throttled-yield (`setTimeout(0)` every ≥100ms); long build phases yield too. */
	signal?: VetoSignal;

	/** **Set6 — the eight per-V-cycle-phase backend selectors.**  Absent → `ALL_CPU` (byte-faithful
	 *  reference).  Each phase routes to its named backend, CPU-fallbacking when unregistered (§5).
	 *  Step 7 deleted the legacy use-GPU booleans; routing now derives from these selectors (§4.4). */
	componentBackends?: ComponentBackendSelection;

	/** **Set6 — preferred coarse smoother variant (lex vs wavefront);** honoured where the
	 *  backend ships both (WebGPU), else the only-available variant is used (WebGL ships
	 *  wavefront only).  Set6 step 7 deleted the legacy `useWavefrontCoarseSmoother` boolean. */
	preferredSmootherVariant?: SmootherVariant;
}

export class SolveAbortedError extends Error {
	constructor(message = "solve aborted via SolveOptions.signal") {
		super(message);
		this.name = "SolveAbortedError";
	}
}

/** Returns `true` iff `e` is a {@link SolveAbortedError} (recognised by the `name` tag, so
 *  it works across `Error`-subclass boundaries / structured clones).  Convenience wrapper
 *  for catch blocks in the camera-pan producer/consumer chain. */
export function isSolveAbortedError(e: unknown): boolean {
	return e instanceof Error && e.name === "SolveAbortedError";
}

/** 2026-08-03 rapid-zoom-queue fix — cooperative-cancellation handle threaded down the whole
 *  solve chain incl. structural build; replaces the plain `AbortSignal` (back-compat `aborted`
 *  passthrough) with `maybeYield()`: throttled `setTimeout(0)` (every ≥100ms) avoids macrotask cost per check site. */
export const YIELD_INTERVAL_MS = 100;

export class VetoSignal {
	/** The underlying AbortController — flipped by `abort()`; read by `aborted`/`addEventListener`. */
	private readonly controller: AbortController;
	/** `Date.now()` of the last `setTimeout(0)` yield this token performed. Initial `0` so the
	 *  first `maybeYield()` call always yields (no warm-up delay). Stamped inside `maybeYield`
	 *  AFTER the `setTimeout(0)` returns (so the throttle window covers the yield's own cost). */
	public lastYieldedAt: number = 0;

	constructor() {
		this.controller = new AbortController();
	}

	/** Passthrough to the inner `AbortController.signal`'s `aborted` flag. */
	get aborted(): boolean { return this.controller.signal.aborted; }

	/** Passthrough to the inner `AbortController`'s `signal` — useful when a callee takes a
	 *  raw `AbortSignal` (e.g. some legacy downstream that hasn't been migrated yet). */
	get signal(): AbortSignal { return this.controller.signal; }

	/** Passthrough to the inner `AbortController.abort()` — flips `aborted` to `true`. */
	abort(): void { this.controller.abort(); }

	/** Throttled `setTimeout(0)` yield — fires only when `Date.now() - lastYieldedAt >=
	 *  YIELD_INTERVAL_MS` (100ms). Consumer: `if (signal?.aborted) throw …; …heavy…;
	 *  await signal.maybeYield(); if (signal?.aborted) throw …;` — post-yield check catches aborts in the window. */
	async maybeYield(): Promise<void> {
		if (Date.now() - this.lastYieldedAt < YIELD_INTERVAL_MS) return;
		await new Promise<void>(r=>setTimeout(r, 0));
		this.lastYieldedAt = Date.now();
	}

	/** Add an `abort` listener — passthrough to the inner `AbortSignal.addEventListener`.
	 *  Kept for back-compat with consumers that subscribe to the abort event (rare; the
	 *  cooperative-cancellation pattern is poll-based via `aborted`). */
	addEventListener(type: "abort", listener: ()=>void): void {
		this.controller.signal.addEventListener(type, listener);
	}
}

/** Returns `true` iff `s` is a {@link VetoSignal} (has `maybeYield`).  Yield-check sites
 *  accept either a plain `AbortSignal` (legacy — no `maybeYield`) or a `VetoSignal`; they
 *  `await s.maybeYield()` only when this returns `true` (a plain `AbortSignal` is a no-op). */
export function isVetoSignal(s: unknown): s is VetoSignal {
	return s instanceof VetoSignal;
}

/** **Test-only instrumentation counters** — incremented by the structural-build entry
 *  points to regression-guard the 2026-07-24 OOM-in-`refineNode` *call-count* explosion.
 *  Plain module-scope increments (free in production).  Reset with {@link resetSolverDiagnostics}. */
export const solverDiagnostics = {
	/** Number of {@link buildCoarseGrid} calls (each one allocates a whole coarse `AMRGrid`
	 *  via `makeUniformGrid` → `refineNode` → `allocCell`, plus its Galerkin CSR stencil). */
	buildCoarseGridCalls: 0,
	/** Number of {@link buildCoarseHierarchy} calls (each one walks the full coarsening chain). */
	buildCoarseHierarchyCalls: 0,
	/** Number of {@link solvePoissonCPU} calls. */
	solvePoissonCPUCalls: 0,
	/** Total leaf-cells allocated across all `buildCoarseGrid` calls — the memory-pressure
	 *  proxy that the OOM pause actually tripped on. */
	coarseCellsAllocated: 0,

	// ---- Set3 WebGPU dispatcher-routing counters (2026-07-25 trace diagnosis). ----
	// The 2026-07-25T02 trace showed the GPU path NOT running despite `useFullGPUCycle` /
	// `useWavefrontCoarseSmoother` enabled — counters distinguish which dispatcher branch fired.
	/** `solvePoisson` calls routed to CPU because NO GPU backend is registered
	 *  (`gpuBackendRef.value == null`).  Non-zero when `terrain.useWebGPU` is false OR init
	 *  failed silently — the counter to check when the profiler still shows CPU `gaussSeidelSweep`. */
	solveDispatch_cpuNoBackend: 0,
	/** `solvePoisson` calls where a backend WAS registered but the grid failed the
	 *  uniform-level gate (`gridIsUniformLevel` false — AMR/mixed-level).  Production solves
	 *  are uniform-fine so this stays 0. */
	solveDispatch_cpuNonUniform: 0,
	/** `solvePoisson` calls that reached `backend.solveAsync` with a uniform grid.
	 *  `gpuPacked − gpuStep1 − gpuStep2` = backend-internal CPU fallbacks (`packCells` null). */
	solveDispatch_gpuPacked: 0,
	/** `solvePoisson` calls that ran the Step 1 path (fine GPU smoother + per-V-cycle CPU
	 *  coarse correction via `applyCoarseGridCorrection`).  Set in `solveAsyncStep1`. */
	solveDispatch_gpuStep1: 0,
	/** `solvePoisson` calls that ran the Step 2 path (full V-cycle on GPU, no inter-level
	 *  H readback).  Set in `solveAsyncFullVCycle` on a successful return. */
	solveDispatch_gpuStep2: 0,
	/** `solvePoisson` calls where the Step 2 allocator returned null (malformed packed
	 *  coarse level) → fell back to Step 1.  Non-zero with `useFullGPUCycle: true` means
	 *  Step 2 isn't actually running. */
	solveDispatch_gpuStep2FellBackToStep1: 0,
	/** `solvePoisson` calls where the GPU backend's `packCells` step returned null and the
	 *  backend itself fell back to `solvePoissonCPU` (`result.backend === "cpu"` — surfaced
	 *  by the `requireGPU` test guard). */
	solveDispatch_gpuBackendInternalCpuFallback: 0,
	// ---- WebGL port counters (2026-07-26 Phase 1 — peer-of-WebGPU-router shape). ----
	// The WebGL2 backend is a SECOND opt-in GPU backend (fallback for unstable-WebGPU hosts);
	// counters mirror the WebGPU ones 1:1 so `useWebGL=true` users can confirm the WebGL path ran.
	/** `solvePoisson` calls that reached the WebGL backend's `solveAsync` with a uniform
	 *  grid.  Non-zero `webglPacked` minus internal CPU fallbacks (texture pool exhausted /
	 *  `gl.OUT_OF_MEMORY` / context-loss hot-fallback). */
	solveDispatch_webglPacked: 0,
	/** `solvePoisson` calls where the WebGL backend's `solveAsync` fell through to
	 *  `solvePoissonCPU` (`result.backend === "cpu"` — the hot-fallback contract on
	 *  `WebGLSolverBackend`). */
	solveDispatch_webglInternalCpuFallback: 0,
	/** `solvePoisson` calls that ran the WebGL Step 2 path (full V-cycle on GL, no inter-
	 *  level H readback via `applyCoarseGridCorrection`).  Set in `solveAsyncFullVCycle_GL`
	 *  on a successful return (peer of `solveDispatch_gpuStep2`). */
	solveDispatch_webglStep2: 0,
	/** `solvePoisson` calls where the WebGL Step 2 allocator returned null (malformed packed
	 *  coarse level) → fell back to Step 1.  Non-zero with `prolong/residual/restrict/galSmoother`
	 *  all `"webGL"` means Step 2 isn't actually running. */
	solveDispatch_webglStep2FellBackToStep1: 0,
	/** **Set3 Step 3 (2026-07-25) — GPU Galerkin build routing counters:** `galerkinGpuCalls` =
	 *  WGSL-shader first-coarsening runs; `galerkinCpuFallbackCalls` = CPU `buildGalerkinStencil`
	 *  fallbacks (no backend / `useGpuGalerkin:false` / inner-coarsening with a parent stencil). */
	galerkinGpuCalls: 0,
	/** Counts CPU `buildGalerkinStencil` fallbacks during a hierarchy build.  See
	 *  {@link galerkinGpuCalls}' docblock for the routing interpretation. */
	galerkinCpuFallbackCalls: 0,
	/** Most recent `GPUSmootherBackend.init()` outcome — `"never"` / `"capability"`
	 *  (no `navigator.gpu`) / `"noAdapter"` / `"noDevice"` / `"pipelineValidation"` / `"ok"` /
	 *  `"errorThrown"`; set by `recordGpuInitOutcome`.  CPU-solve traces with `!== "ok"` = smoking gun. */
	gpuInitOutcome: "never" as "never" | "capability" | "noAdapter" | "noDevice" | "pipelineValidation" | "ok" | "errorThrown",
	/** Most recent `GPUSmootherBackend.init()` failure detail (the Error stringifier or a
	 *  short reason) so a console log shows WHY init failed.  Reset to `null` on success. */
	gpuInitFailureDetail: null as string | null,
	// ---- WebGL port init-outcome diagnostics (2026-07-26 Phase 1). ----
	// Mirror of `gpuInitOutcome`/`gpuInitFailureDetail` for the WebGL2 backend — `getDiagnostics`
	// + `prepareTerrainDone` forward both; a non-`ok` outcome = silent fallback (WebGL path didn't run).
	/** Most recent `WebGLSolverBackend.init()` outcome — one of {@link WebGLInitOutcome}'s
	 *  values (`"never"` / `"ok"` / `"noOffscreenCanvas"` / `"getContextFailed"` /
	 *  `"noColorBufferFloat"` / `"errorThrown"`); set by `recordWebglInitOutcome` (from `ensureWebGLBackendInit`). */
	webglInitOutcome: "never" as "never" | "ok" | "noOffscreenCanvas" | "getContextFailed" | "noColorBufferFloat" | "errorThrown",
	/** Most recent `WebGLSolverBackend.init()` failure detail (the Error stringifier or
	 *  short reason).  Reset to `null` on a successful init. */
	webglInitFailureDetail: null as string | null,
};

/** Zero every counter in {@link solverDiagnostics}.  Call at the top of a test. */
export function resetSolverDiagnostics(): void {
	solverDiagnostics.buildCoarseGridCalls = 0;
	solverDiagnostics.buildCoarseHierarchyCalls = 0;
	solverDiagnostics.solvePoissonCPUCalls = 0;
	solverDiagnostics.coarseCellsAllocated = 0;
	solverDiagnostics.solveDispatch_cpuNoBackend = 0;
	solverDiagnostics.solveDispatch_cpuNonUniform = 0;
	solverDiagnostics.solveDispatch_gpuPacked = 0;
	solverDiagnostics.solveDispatch_gpuStep1 = 0;
	solverDiagnostics.solveDispatch_gpuStep2 = 0;
	solverDiagnostics.solveDispatch_gpuStep2FellBackToStep1 = 0;
	solverDiagnostics.solveDispatch_gpuBackendInternalCpuFallback = 0;
	solverDiagnostics.solveDispatch_webglPacked = 0;
	solverDiagnostics.solveDispatch_webglInternalCpuFallback = 0;
	solverDiagnostics.solveDispatch_webglStep2 = 0;
	solverDiagnostics.solveDispatch_webglStep2FellBackToStep1 = 0;
	solverDiagnostics.galerkinGpuCalls = 0;
	solverDiagnostics.galerkinCpuFallbackCalls = 0;
	solverDiagnostics.gpuInitOutcome = "never";
	solverDiagnostics.gpuInitFailureDetail = null;
	solverDiagnostics.webglInitOutcome = "never";
	solverDiagnostics.webglInitFailureDetail = null;
}

/** Record a `GPUSmootherBackend.init()` outcome in {@link solverDiagnostics} so the worker
 *  can surface it in `getDiagnostics`; called from `TerrainWorker.ensureGPUBackendInit` AND
 *  the browser-lane test's `beforeAll` (regression surfaces as `gpuInitOutcome !== "ok"`). */
export function recordGpuInitOutcome(
	outcome: "capability" | "noAdapter" | "noDevice" | "pipelineValidation" | "ok" | "errorThrown",
	detail: string | null = null,
): void {
	solverDiagnostics.gpuInitOutcome = outcome;
	solverDiagnostics.gpuInitFailureDetail = detail;
}

/** Record a `WebGLSolverBackend.init()` outcome in {@link solverDiagnostics} — the WebGL
 *  peer of `recordGpuInitOutcome`; called from `ensureWebGLBackendInit` AND the browser-lane
 *  test's `beforeAll`.  Union mirrors `WebGLInitOutcome` so the worker path stays WebGL-import-free when off. */
export function recordWebglInitOutcome(
	outcome: "ok" | "noOffscreenCanvas" | "getContextFailed" | "noColorBufferFloat" | "errorThrown",
	detail: string | null = null,
): void {
	solverDiagnostics.webglInitOutcome = outcome;
	solverDiagnostics.webglInitFailureDetail = detail;
}

/** Functional interface for a Poisson solver backend.  CPU = {@link solvePoissonCPU} (yields
 *  between V-cycles via `setTimeout(0)` when `opts.signal` is present — 2026-08-03 fix); GPU =
 *  {@link solvePoissonGPU} via {@link setGPUSolverBackend} — replaces fine-level smoothing with WGSL shaders. */
export interface SolverBackend {
	/** Solve in-place on `grid` (uniform-fine at the reference level — AMR/coarse-grid
	 *  GPU porting is Step 2 of the plan).  Returns the same {@link SolveResult} shape
	 *  the CPU backend produces so callers don't care which path ran. */
	solveAsync(grid: AMRGrid, cfg: SolverConfig, opts?: SolveOptions): Promise<SolveResult>;
}

